"use client";
import React from "react";

import {
  getResponsiveStyle,
  ResponsiveProp
} from "../../utils/getResponsiveStyle";
import { Box, BoxDiv, BoxSx, IconNameType, ThemeColors } from "..";
import { useIconCache } from "./IconContext";

export interface SvgIconProps {
  fill?: "none" | "filled" | string;
}

export type IconProps = {
  size?: ResponsiveProp<"xxs" | "xs" | "sm" | "md" | "lg" | "xl">;
  weight?: "regular" | "filled";
  icon: IconNameType;
  color?: ThemeColors;
} & Omit<BoxDiv, "size">;

export const Icon: React.FC<IconProps> = ({
  size = "md",
  weight = "regular",
  sx,
  icon,
  color,
  ...restProps
}) => {
  const { getIconComponent, cacheIconComponent } = useIconCache();
  const LazyIconVariant = getIconComponent(icon);

  React.useEffect(() => {
    cacheIconComponent(icon, LazyIconVariant);
  }, [icon, LazyIconVariant, cacheIconComponent]);

  const iconSize = React.useMemo<BoxSx["width"]>(
    () =>
      getResponsiveStyle(size, (s) => {
        switch (s) {
          case "xxs":
            return "spacing-5";
          case "xs":
            return "spacing-6";
          case "sm":
            return "spacing-7";
          case "lg":
            return "spacing-9";
          case "xl":
            return "spacing-10";
          default:
            return "spacing-8";
        }
      }),
    [size]
  );

  const fill = weight === "filled" ? "filled" : "none";

  return (
    <Box
      as="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: color || "onSurface-brand-primary",
        height: iconSize,
        width: iconSize,
        ...sx
      }}
      {...restProps}
    >
      <LazyIconVariant fill={fill} />
    </Box>
  );
};
